import React, { lazy, useMemo } from 'react';
import { DefaultIndustrial } from './IndustrialImageComponent';
import {
    ApplicationsContent,
    ApplicationsIndustrialIndustrialImageMapLinkTextsContent,
} from '../../graphql/pimcore/modules/siteContent/types/ApplicationsContent';
import { AutoImageProps } from '../automotivePage/CarImageComponent';

export interface ComponentInfo {
    label: string;
    component?: React.ComponentType<any>;
    imageComponent?: React.ComponentType<any>;
}

const LowVoltDcSwitchAcImage = lazy(() =>
    import('../industrialPage/IndustrialImageComponent').then((module) => ({
        default: module.LowVoltDcSwitchAcImage,
    }))
);
const TractionInverterImage = lazy(() =>
    import('../computingPage/CommercialSVGs').then((module) => ({
        default: module.TractionInverterImage,
    }))
);
const PowerConversionImage = lazy(() =>
    import('../computingPage/CommercialSVGs').then((module) => ({
        default: module.PowerConversionImage,
    }))
);

export function generateLabelToComponentMap(
    t: ApplicationsIndustrialIndustrialImageMapLinkTextsContent
): Record<string, string> {
    return {
        [t.gridInfrastructureAndMetering.content]: 'gridinfrastructureandmetering',
        [t.renewableAndGreenEnergy.content]: 'renewableandgreenenergy',
        [t.testAndMeasurement.content]: 'testandmeasurement',
        [t.evCharging.content]: 'evcharging',
        [t.buildingAutomationAndFactoryAutomation.content]:
            'buildingautomationandfactoryautomation',
        [t.motorDrivesAndTransport.content]: 'motordrivesandtransport',
        [t.lightingAndDigitalSignage.content]: 'lightinganddigitalsignage',
        [t.hvac.content]: 'hvac',
        [t.powerSupply.content]: 'powersupply',
    };
}

function withContentTree<P>(
    Component: React.ComponentType<P & { contentTree: ApplicationsContent }>,
    contentTree: ApplicationsContent
): React.ComponentType<P> {
    const WrappedComponent = (props: P) =>
        React.createElement(Component, { ...props, contentTree });
    const componentName = Component.displayName || Component.name || 'Component';
    WrappedComponent.displayName = `withContentTree(${componentName})`;

    return WrappedComponent;
}

export const useIndustrialComponents = (
    contentTree: ApplicationsContent
): {
    components: { [key: string]: ComponentInfo };
} => {
    const components = useMemo<{ [key: string]: ComponentInfo }>(() => {
        const t = contentTree.Industrial.Industrial.imageMapLinkTexts;

        return {
            industrial: {
                label: contentTree.Industrial.Industrial.title.content,
                component: withContentTree(DefaultIndustrial, contentTree),
            },
            gridinfrastructureandmetering: {
                label: t.gridInfrastructureAndMetering.content,
                imageComponent: withContentTree(LowVoltDcSwitchAcImage, contentTree),
            },
            renewableandgreenenergy: {
                label: t.renewableAndGreenEnergy.content,
                imageComponent: withContentTree(LowVoltDcSwitchAcImage, contentTree),
            },
            testandmeasurement: {
                label: t.testAndMeasurement.content,
                imageComponent: withContentTree(LowVoltDcSwitchAcImage, contentTree),
            },
            evcharging: {
                label: t.evCharging.content,
                imageComponent: PowerConversionImage,
            },
            buildingautomationandfactoryautomation: {
                label: t.buildingAutomationAndFactoryAutomation.content,
                imageComponent: withContentTree(LowVoltDcSwitchAcImage, contentTree),
            },
            motordrivesandtransport: {
                label: t.motorDrivesAndTransport.content,
                imageComponent: TractionInverterImage,
            },
            lightinganddigitalsignage: {
                label: t.lightingAndDigitalSignage.content,
                imageComponent: withContentTree(LowVoltDcSwitchAcImage, contentTree),
            },
            hvac: {
                label: t.hvac.content,
                imageComponent: PowerConversionImage,
            },
            powersupply: {
                label: t.powerSupply.content,
                imageComponent: PowerConversionImage,
            },
        };
    }, [contentTree]);

    return { components };
};
export type ComponentName = keyof ReturnType<typeof useIndustrialComponents>['components'];
